import { ReactNode } from 'react';

import {
  CAMPAIGN_NAME,
  DELAYED_SHIPPING_EXPIRATION,
  DISABLE_SHIPMENT_TRACKING,
  EXPERIENCE_ID,
  HAS_LEGACY_CLAIM_PAGE_CONFIG,
  INTERNAL_NOTES,
  IS_TOGGLE_ON_HOLD_ENABLED,
  RECEIVER_REMINDER_INTERVAL,
  SEND_ON_BEHALF_THE_EMAIL,
  SEND_ON_HOLD,
} from '../constants/bucket';
import { BUSINESS_REASON_REQUIRED, DISABLE_HANDWRITTEN_NOTE_STEP, ORG_ADDRESS_IDS } from '../constants/campaigns';
import { TITLE } from '../constants/oneLinkKeys';
import { BUSINESS_REASON_ID } from '../constants/reasons';
import { ENGAGEMENT_SOURCE_TYPE } from '../constants/reports';
import { SupportedFlowsEnum } from '../constants/routing';
import {
  ALLOWED_DOMAINS,
  DISABLE_RECIPIENT_SHIPPED_EMAIL,
  DISABLE_SENDER_EMAIL_NOTIFICATIONS,
  ENABLED_2FA,
  EXPIRATION_DATE,
  IS_OFFICE_DROPSHIP_ENABLED,
  IS_RECEIVER_ADDRESS_FIXED,
  PASSWORD,
  RECEIVER_FIXED_ADDRESS,
  RECIPIENTS,
  RECIPIENTS_COUNT,
  REDEMPTION_LIMIT,
  SHIPPING_OPTION,
  STATUS,
  TContactForm,
  TCRMForm,
  TShippingForm,
} from '../constants/shipping';
import { ICampaign, ICampaignItem } from './campaigns';
import { IMappedMskuItem, IOnDemandItem, IRequestOnDemandItem } from './inventories';
import { IGiftBudget, IMarketplaceItem, MARKETPLACE_SOURCE, TMarketplaceRequestMap } from './marketplace';
import { IOneLinkRecipients, TOneLinkRecipient } from './oneLink';
import { IPromiseCallbacks } from './redux';
import { EngagementSourceTypesEnum, IReportEngagementItem, ReportTypesEnum } from './reports';
import {
  IBulkPayloadProps,
  ICommonAddress,
  ISendDetailsFormValues,
  IShippingDetails,
  ShippingOptionsEnum,
} from './shipping';
import { IAddEmailFormValues, IAddNoteFormValues } from './templates';

// Its own variant rather than a cast to `ICampaignItem`, so selectors reading inventory-only
// fields (`type`, `preorder_allowed`) fail to compile instead of reading `undefined`.
// No `is_hidden_for_recipient`: that treatment is IE inventory only.
export type TMarketplaceBucketItem = IMarketplaceItem & {
  quantity: number;
};

export type TBucketItem = ICampaignItem | IOnDemandItem;

export type TCreateSendBucketItem = TBucketItem | TMarketplaceBucketItem;

export const isMarketplaceBucketItem = (item: TCreateSendBucketItem): item is TMarketplaceBucketItem =>
  (item as TMarketplaceBucketItem).source === MARKETPLACE_SOURCE;

export type TEngagementCandidate = Partial<IAddEmailFormValues> &
  Partial<IAddNoteFormValues> &
  Partial<TCRMForm> &
  Partial<Omit<TShippingForm, 'receiver_address' | 'status'>> &
  ISummaryFormFields & {
    items?: (ICampaignItem | IOnDemandItem)[] | null;
    pre_created_engagement_id?: string;
    [SEND_ON_BEHALF_THE_EMAIL]?: string;
    pick_campaigns?: ICampaign[] | null;
    pick_campaign_ids?: string[];
    [RECEIVER_REMINDER_INTERVAL]?: string | null;
    [DISABLE_SENDER_EMAIL_NOTIFICATIONS]?: boolean;
    [DISABLE_RECIPIENT_SHIPPED_EMAIL]: boolean;
    status?: string;
    receiver_company_name?: string;
    receiver_address1?: string;
    receiver_address2?: string;
    receiver_city?: string;
    receiver_country?: string;
    receiver_zip?: string;
    receiver_state?: string;
    delayed_shipping_url?: string;
    [CAMPAIGN_NAME]: string;
    one_link_id?: string;
    receiver_tax_number?: string;
    created_at?: string;
    delayed_shipping_page_view_date?: string;
    receiver_next_reminder_send_date?: string;
    experience_id?: string;
    marketplace_items?: TMarketplaceBucketItem[] | null;
    marketplace_pyg_ids?: string[] | null;
    gift_budget?: IGiftBudget | null;
  };

export interface IOneLinkEngagementCandidate
  extends Partial<IAddEmailFormValues>,
    Partial<IAddNoteFormValues>,
    ISummaryFormFields {
  items?: (ICampaignItem | IOnDemandItem)[] | null;
  pre_created_engagement_id?: string;
  pick_campaigns?: ICampaign[] | null;
  pick_campaign_ids?: string[];
  expiration_date?: string | null;
  [SEND_ON_BEHALF_THE_EMAIL]?: string;
  disable_sender_email_notifications: boolean;
  [DISABLE_RECIPIENT_SHIPPED_EMAIL]: boolean;
  [RECEIVER_REMINDER_INTERVAL]?: string | null;
  [PASSWORD]?: string;
  allowed_domains?: string[];
  [ENABLED_2FA]: boolean;
  [RECIPIENTS]: TOneLinkRecipient[] | null | undefined;
  [REDEMPTION_LIMIT]?: number | null;
  [RECIPIENTS_COUNT]?: undefined;
  [RECEIVER_FIXED_ADDRESS]?: ICommonAddress | undefined;
  [IS_RECEIVER_ADDRESS_FIXED]?: boolean;
  [IS_OFFICE_DROPSHIP_ENABLED]?: boolean;
  [ORG_ADDRESS_IDS]?: string[];
  [SHIPPING_OPTION]?: ShippingOptionsEnum;
  [STATUS]?: never;
  [TITLE]?: string;
  [CAMPAIGN_NAME]: string;
  experience_id?: string;
  marketplace_items?: TMarketplaceBucketItem[] | null;
  marketplace_pyg_ids?: string[] | null;
  gift_budget?: IGiftBudget | null;
}

export type TMarketplaceCandidateField = 'marketplace_items' | 'marketplace_pyg_ids' | 'gift_budget';

export type TEngagementRequestPayload<T = TEngagementCandidate | IOneLinkEngagementCandidate> = Omit<
  T,
  'items' | 'disable_sender_email_notifications' | 'marketplace_items' | 'marketplace_pyg_ids'
> & {
  items?: (ICampaignItem | IRequestOnDemandItem | IMappedMskuItem)[] | null;
  marketplace?: TMarketplaceRequestMap | null;
} & (T extends IOneLinkEngagementCandidate
    ? { disable_email_notifications?: boolean }
    : {
        disable_sender_email_notifications?: boolean;
      });

export interface IErrorResponse {
  message: string;
  error_code?: string;
}

export interface IBucketState {
  flow: SupportedFlowsEnum | null;
  bulk: {
    [DISABLE_SHIPMENT_TRACKING]: boolean;
    csv: IFileInfo | null;
    result: IBulkEngagementResponse | null;
    isLoading: boolean;
    isSubmitted: boolean;
  };
  expiration_date: string | null;
  pre_created_engagement_id: string;
  engagement_source_type: string;
  [DISABLE_HANDWRITTEN_NOTE_STEP]?: boolean;
  [PASSWORD]?: string;
  [ALLOWED_DOMAINS]?: string[];
  [ENABLED_2FA]: boolean;
  [RECIPIENTS]: IOneLinkRecipients | null;
  [BUSINESS_REASON_ID]: string;
  [INTERNAL_NOTES]: string;
  [SEND_ON_HOLD]: string;
  [DELAYED_SHIPPING_EXPIRATION]: string;
  [SEND_ON_BEHALF_THE_EMAIL]: string | null;
  receiverDetails: Partial<TContactForm | (TContactForm & TCRMForm)> | null;
  shippingDetails: IShippingDetails;
  [SHIPPING_OPTION]: ShippingOptionsEnum;
  note: IAddNoteFormValues;
  isNoteSkipped?: boolean;
  email: IAddEmailFormValues;
  pick_campaigns: ICampaign[] | null;
  items: TBucketItem[];
  isFetching: boolean;
  error: IErrorResponse | null;
  [DISABLE_SENDER_EMAIL_NOTIFICATIONS]: boolean;
  [IS_TOGGLE_ON_HOLD_ENABLED]?: boolean;
  receiver_reminder_interval?: string | null;
  [REDEMPTION_LIMIT]?: number | null;
  [BUSINESS_REASON_REQUIRED]?: boolean;
  [TITLE]?: string;
  [CAMPAIGN_NAME]: string;
  [DISABLE_RECIPIENT_SHIPPED_EMAIL]: boolean;
  [EXPERIENCE_ID]?: string;
  // Snapshot of "the chosen campaign is customized only through the legacy ds_* fields", taken when
  // the campaign is confirmed. The campaigns list is a single page that is dropped on persist and
  // replaced on every filter/search, so the claim page template step cannot re-derive this later.
  [HAS_LEGACY_CLAIM_PAGE_CONFIG]?: boolean;
}

export interface ISummaryFormFields {
  [INTERNAL_NOTES]?: string;
  [BUSINESS_REASON_ID]?: string;
  [SEND_ON_HOLD]?: string;
  [DELAYED_SHIPPING_EXPIRATION]?: string;
  [ENGAGEMENT_SOURCE_TYPE]?: EngagementSourceTypesEnum;
}

export interface IOneLinkFormFields {
  [IS_RECEIVER_ADDRESS_FIXED]?: boolean;
  [ORG_ADDRESS_IDS]?: string[];
  [EXPIRATION_DATE]: string | null;
  [DISABLE_SENDER_EMAIL_NOTIFICATIONS]: boolean;
  [PASSWORD]?: string;
  [ALLOWED_DOMAINS]?: string[];
  [ENABLED_2FA]: boolean;
  [REDEMPTION_LIMIT]?: number | null;
  [TITLE]?: string;
  [DISABLE_RECIPIENT_SHIPPED_EMAIL]: boolean;
}

export interface IDigitalCard {
  name: string;
  url: string;
}

export interface IFileInfo {
  name: string;
  lastModified: number;
  size: number;
}

export interface IBulkEngagementSubmitProps {
  data: IBulkEngagementRequest;
  resolve: (...args: any[]) => void;
  reject: (...args: any[]) => void;
}

export type TFlowStepSlug = 'box' | 'shipping' | 'note' | 'email' | 'upload' | 'summary' | 'claim-page-template';

export interface IFlowStep {
  slug: string;
  next?: string;
  prev?: string;
}

export interface IFlowStepProps {
  flowId?: SupportedFlowsEnum;
  stepId?: string;
  next?: string;
  prev?: string;
  isBulk?: boolean;
  isSingle?: boolean;
  isOneLink?: boolean;
  isCustomSend?: boolean;
  isDirectSend?: boolean;
  onBoxSelect?: (...args: any[]) => void;
  onDelayedShippingChange?: (isDS: boolean) => void;
  SenderInfo?: ReactNode | undefined;
  isPYG?: boolean;
  isFlowMetadataLoading?: boolean;
  hasMsku?: boolean;
  hasCustomizableItems?: boolean;
}

export interface IFlowValidatedStep extends IFlowStep {
  valid: boolean;
}

export interface IChangeBucketItemPayload {
  id: string;
  value: string | boolean | number;
  name: string;
}

//#region Requests Interfaces

export interface IPostEngagementRequestPayload {
  engagement: TEngagementCandidate | IOneLinkEngagementCandidate;
  metadata: Omit<IFlowStepProps, 'className' | 'SenderInfo' | 'isPYG'>;
}

export interface ISendItemsInfo {
  items_info: IReportEngagementItem[];
  receiver_info?: never;
  shipping_info?: never;
}

export interface ISendReceiverInfo {
  receiver_info: Partial<ISendDetailsFormValues>;
  items_info?: never;
  shipping_info?: never;
}

export interface ISendShippingInfo {
  shipping_info: IBulkPayloadProps;
  items_info?: never;
  receiver_info?: never;
}

export type TEditEngagementPayload = ISendItemsInfo | ISendReceiverInfo | ISendShippingInfo;

export interface IEditEngagementRequestPayload extends IPromiseCallbacks {
  engagement: { type: ReportTypesEnum; engagement_id: string } & TEditEngagementPayload;
}

export interface IEditSendFieldRequestPayload {
  key: string;
  value: string | string[];
}

export interface IEditEngagementSuccessPayload {
  engagement: TEditEngagementPayload & {
    type: ReportTypesEnum;
    engagement_id: string;
  };
}

export interface IBulkEngagementResponse {
  success_count: number;
  error_count: number;
  success_file_url: string;
  error_file_url: string;
}

export interface ICreateEngagementResponse {
  order_id: string;
  digital_gifts: IDigitalCard[] | null;
  delayed_shipping_url?: string;
}

export interface ICreateOneLinkSendResponse {
  url?: string;
  password?: string;
  invalid_recipients?: TOneLinkRecipient[];
}

export interface IBulkEngagementRequest {
  file_name: string;
  pre_created_engagement_id: string;
  items: any[]; // TODO ...
  org_id: string;
  disable_sender_email_notifications: boolean;
  note_template: string;
  email_message: string;
  email_subject: string;
  email_template_id: string;
  on_hold_until: string;
  business_reason_id: string;
  status: string;
  is_receiver_address_fixed: boolean;
  org_address_ids?: string[];
  receiver_address?: ICommonAddress;
  [CAMPAIGN_NAME]: string;
}

//#endregion
